import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Shield, UserCheck, Car, CheckCircle, Plane, Luggage, User, CreditCard, Repeat, ArrowRight, CalendarIcon, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const includedServices = [
  { icon: Shield, label: "Free Cancellation" },
  { icon: UserCheck, label: "Meet & Greet" },
  { icon: Car, label: "Private Transfer" },
  { icon: CheckCircle, label: "Licensed Chauffeurs" },
];

interface BookingState {
  from: string;
  to: string;
  fromPlaceId?: string;
  toPlaceId?: string;
  routeType: string;
  passengers: number;
  promoCode?: string;
  distanceKm: number | null;
  durationMin: number | null;
  vehicle: {
    id: string;
    name: string;
    image_url: string | null;
    price: number;
    capacity: number;
  };
}

const timeSlots = Array.from({ length: 48 }, (_, i) => {
  const h = Math.floor(i / 2);
  const m = i % 2 === 0 ? "00" : "30";
  return `${h.toString().padStart(2, "0")}:${m}`;
});

const BookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as BookingState | null;
  // Form state - must be before any early returns
  const [arrivalDate, setArrivalDate] = useState<Date>();
  const [arrivalTime, setArrivalTime] = useState("");
  const [arrivalAirline, setArrivalAirline] = useState("");
  const [arrivalFlightNumber, setArrivalFlightNumber] = useState("");

  const [returnDate, setReturnDate] = useState<Date>();
  const [returnTime, setReturnTime] = useState("");
  const [returnAirline, setReturnAirline] = useState("");
  const [returnFlightNumber, setReturnFlightNumber] = useState("");

  const [luggage, setLuggage] = useState("2");
  const [remarks, setRemarks] = useState("");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const [paymentMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeMethods, setActiveMethods] = useState<{ method: string; is_active: boolean }[]>([]);

  useEffect(() => {
    supabase
      .from("payment_methods")
      .select("method, is_active")
      .eq("is_active", true)
      .then(({ data }) => {
        const methods = (data as any[]) ?? [];
        setActiveMethods(methods);
        if (methods.length > 0 && !paymentMethod) {
          setPaymentMethod(methods[0].method);
        }
      });
  }, []);

  // Redirect if no state
  if (!state) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground font-sans">No booking information found.</p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  const { from, to, fromPlaceId, toPlaceId, routeType, passengers, vehicle, distanceKm, durationMin } = state;

  const isFromAirport = fromPlaceId?.startsWith("db-airport-");
  const isToAirport = toPlaceId?.startsWith("db-airport-");
  const showArrivalFlightFields = !!isFromAirport;
  const showReturnSection = routeType === "round-trip";
  const showReturnFlightFields = !!(isFromAirport || isToAirport);

  const handleReserve = async () => {
    if (!arrivalDate || !arrivalTime || !fullName || !email || !phone) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (showReturnSection && (!returnDate || !returnTime)) {
      toast.error("Please fill in return date and time.");
      return;
    }

    setSubmitting(true);

    const pickupDateTime = new Date(arrivalDate);
    const [h, m] = arrivalTime.split(":").map(Number);
    pickupDateTime.setHours(h, m, 0, 0);

    let returnDateTime: Date | null = null;
    if (showReturnSection && returnDate && returnTime) {
      returnDateTime = new Date(returnDate);
      const [rh, rm] = returnTime.split(":").map(Number);
      returnDateTime.setHours(rh, rm, 0, 0);
    }

    const flightNum = [
      arrivalFlightNumber,
      returnFlightNumber ? `Return: ${returnFlightNumber}` : null,
    ]
      .filter(Boolean)
      .join(" | ") || null;

    const { data: booking, error } = await supabase.from("bookings").insert({
      pickup_location: from,
      dropoff_location: to,
      route_type: routeType,
      passengers,
      pickup_date: pickupDateTime.toISOString(),
      return_date: returnDateTime?.toISOString() || null,
      flight_number: flightNum,
      customer_name: fullName,
      customer_email: email,
      customer_phone: phone,
      vehicle_id: vehicle.id,
      total_price: vehicle.price,
      special_requests: [
        `Luggage: ${luggage}`,
        arrivalAirline ? `Airline: ${arrivalAirline}` : null,
        returnAirline ? `Return Airline: ${returnAirline}` : null,
        remarks || null,
      ]
        .filter(Boolean)
        .join(" | ") || null,
      distance_km: distanceKm,
      estimated_time_min: durationMin,
      payment_status: paymentMethod === "cash" ? "unpaid" : "pending",
      payment_method: paymentMethod,
    } as any).select().single();

    setSubmitting(false);

    if (error) {
      toast.error("Failed to create booking. Please try again.");
      console.error(error);
    } else if (paymentMethod === "paypal") {
      // Redirect to PayPal payment page
      navigate("/payment", {
        state: {
          bookingId: booking.id,
          bookingCode: booking.booking_code,
          from,
          to,
          passengers,
          routeType: routeType,
          totalPrice: vehicle.price,
          pickupDate: booking.pickup_date,
          returnDate: booking.return_date,
          distanceKm: booking.distance_km,
          estimatedTimeMin: booking.estimated_time_min,
          customerName: booking.customer_name,
          customerEmail: booking.customer_email,
          customerPhone: booking.customer_phone,
          flightNumber: booking.flight_number,
          specialRequests: booking.special_requests,
          paymentMethod: booking.payment_method,
          vehicle: {
            name: vehicle.name,
            image_url: vehicle.image_url,
          },
        },
      });
    } else {
      // Fire and forget: send confirmation email for cash bookings
      supabase.functions.invoke("send-booking-email", {
        body: { booking_id: booking.id },
      }).catch((err) => console.error("Email send error:", err));

      toast.success("Booking created successfully!");
      navigate("/confirmation", {
        state: {
          booking,
          vehicle: {
            name: vehicle.name,
            image_url: vehicle.image_url,
          },
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <div className="container mx-auto px-4 py-8 pt-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT: Form */}
          <div className="lg:col-span-2 space-y-8">
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">
              Complete Your <span className="text-primary">Booking</span>
            </h1>

            {/* A) Flight / Schedule Information */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="p-6 rounded-2xl bg-card border border-border space-y-6">
              <div className="flex items-center gap-2">
                <Plane className="w-5 h-5 text-primary" />
                <h2 className="font-display text-lg font-bold text-foreground">
                  {showArrivalFlightFields ? "Flight Information" : "Schedule Information"}
                </h2>
              </div>

              {/* Arrival */}
              <div className="space-y-4">
                <p className="text-sm font-sans font-semibold text-muted-foreground uppercase tracking-wide">
                  {showArrivalFlightFields ? "Arrival Flight" : "Pickup Details"}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-sans">Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal font-sans", !arrivalDate && "text-muted-foreground")}>
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {arrivalDate ? format(arrivalDate, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={arrivalDate} onSelect={setArrivalDate} disabled={(d) => d < new Date()} initialFocus className="p-3 pointer-events-auto" />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label className="font-sans">Time *</Label>
                    <Select value={arrivalTime} onValueChange={setArrivalTime}>
                      <SelectTrigger className="font-sans"><SelectValue placeholder="Select time" /></SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {showArrivalFlightFields && (
                    <>
                      <div className="space-y-2">
                        <Label className="font-sans">Airline</Label>
                        <Input placeholder="e.g. JetBlue" value={arrivalAirline} onChange={(e) => setArrivalAirline(e.target.value)} className="font-sans" />
                      </div>
                      <div className="space-y-2">
                        <Label className="font-sans">Flight Number</Label>
                        <Input placeholder="e.g. B6 1234" value={arrivalFlightNumber} onChange={(e) => setArrivalFlightNumber(e.target.value)} className="font-sans" />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Return */}
              {showReturnSection && (
                <div className="space-y-4 pt-4 border-t border-border">
                  <p className="text-sm font-sans font-semibold text-muted-foreground uppercase tracking-wide">
                    {showReturnFlightFields ? "Return Flight" : "Return Details"}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="font-sans">Return Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className={cn("w-full justify-start text-left font-normal font-sans", !returnDate && "text-muted-foreground")}>
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {returnDate ? format(returnDate, "PPP") : "Select date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar mode="single" selected={returnDate} onSelect={setReturnDate} disabled={(d) => d < new Date()} initialFocus className="p-3 pointer-events-auto" />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-sans">Return Time *</Label>
                      <Select value={returnTime} onValueChange={setReturnTime}>
                        <SelectTrigger className="font-sans"><SelectValue placeholder="Select time" /></SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((t) => <SelectItem key={`r-${t}`} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {showReturnFlightFields && (
                      <>
                        <div className="space-y-2">
                          <Label className="font-sans">Return Airline</Label>
                          <Input placeholder="e.g. American Airlines" value={returnAirline} onChange={(e) => setReturnAirline(e.target.value)} className="font-sans" />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-sans">Return Flight Number</Label>
                          <Input placeholder="e.g. AA 5678" value={returnFlightNumber} onChange={(e) => setReturnFlightNumber(e.target.value)} className="font-sans" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </motion.div>

            {/* B) Extra Information */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="p-6 rounded-2xl bg-card border border-border space-y-6">
              <div className="flex items-center gap-2">
                <Luggage className="w-5 h-5 text-primary" />
                <h2 className="font-display text-lg font-bold text-foreground">Extra Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-sans">Amount of Luggage</Label>
                  <Select value={luggage} onValueChange={setLuggage}>
                    <SelectTrigger className="font-sans"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 20 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-sans">Remarks / Special Instructions</Label>
                <Textarea placeholder="Child seat needed, wheelchair accessible, etc." value={remarks} onChange={(e) => setRemarks(e.target.value)} className="font-sans" />
              </div>
            </motion.div>

            {/* C) Personal Information */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="p-6 rounded-2xl bg-card border border-border space-y-6">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                <h2 className="font-display text-lg font-bold text-foreground">Personal Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label className="font-sans">Full Name *</Label>
                  <Input placeholder="John Doe" value={fullName} onChange={(e) => setFullName(e.target.value)} className="font-sans" />
                </div>
                <div className="space-y-2">
                  <Label className="font-sans">Email *</Label>
                  <Input type="email" placeholder="john@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className="font-sans" />
                </div>
                <div className="space-y-2">
                  <Label className="font-sans">Phone *</Label>
                  <Input type="tel" placeholder="+1 809 555 1234" value={phone} onChange={(e) => setPhone(e.target.value)} className="font-sans" />
                </div>
              </div>
            </motion.div>

            {/* D) Payment Method */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="p-6 rounded-2xl bg-card border border-border space-y-6">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                <h2 className="font-display text-lg font-bold text-foreground">Payment Method</h2>
              </div>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                {activeMethods.some((m) => m.method === "cash") && (
                  <label className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-primary/30 cursor-pointer transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <RadioGroupItem value="cash" />
                    <div>
                      <p className="font-sans font-medium text-foreground">Cash</p>
                      <p className="text-xs text-muted-foreground font-sans">Pay your driver directly</p>
                    </div>
                  </label>
                )}
                {activeMethods.some((m) => m.method === "paypal") && (
                  <label className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-primary/30 cursor-pointer transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <RadioGroupItem value="paypal" />
                    <div>
                      <p className="font-sans font-medium text-foreground">PayPal</p>
                      <p className="text-xs text-muted-foreground font-sans">Pay securely with PayPal</p>
                    </div>
                  </label>
                )}
                {activeMethods.some((m) => m.method === "stripe") && (
                  <label className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-primary/30 cursor-pointer transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <RadioGroupItem value="stripe" />
                    <div>
                      <p className="font-sans font-medium text-foreground">Credit / Debit Card</p>
                      <p className="text-xs text-muted-foreground font-sans">Pay securely with Stripe</p>
                    </div>
                  </label>
                )}
              </RadioGroup>

              <Button
                size="lg"
                className="w-full font-sans font-bold text-base mt-4"
                onClick={handleReserve}
                disabled={submitting}
              >
                {submitting ? "Processing..." : "Reserve Now"}
              </Button>
            </motion.div>
          </div>

          {/* RIGHT: Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 p-6 rounded-2xl bg-card border border-border space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-bold text-foreground">Your Booking</h3>
                <Badge variant="secondary" className="font-sans text-xs gap-1.5">
                  {routeType === "round-trip" ? (
                    <><Repeat className="w-3 h-3" /> Round Trip</>
                  ) : (
                    <><ArrowRight className="w-3 h-3" /> One Way</>
                  )}
                </Badge>
              </div>

              {/* Vehicle */}
              <div className="rounded-xl overflow-hidden border border-border">
                <img
                  src={vehicle.image_url || "/placeholder.svg"}
                  alt={vehicle.name}
                  className="w-full h-36 object-cover"
                  loading="lazy"
                />
                <div className="p-3">
                  <p className="font-display font-bold text-foreground">{vehicle.name}</p>
                  <p className="text-xs text-muted-foreground font-sans">Up to {vehicle.capacity} passengers</p>
                </div>
              </div>

              {/* Trip details */}
              <div className="space-y-3 text-sm font-sans">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Pick-up</p>
                    <p className="text-foreground font-medium">{from}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 rounded-full bg-gold-light mt-1.5 shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Drop-off</p>
                    <p className="text-foreground font-medium">{to}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Passengers</span>
                  <span className="text-foreground font-medium">{passengers}</span>
                </div>
                {distanceKm && (
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Distance</span>
                    <span className="text-foreground font-medium">{distanceKm} km</span>
                  </div>
                )}
                {durationMin && (
                  <div className="flex items-center justify-between text-muted-foreground">
                    <span>Est. Time</span>
                    <span className="text-foreground font-medium">~{durationMin} min</span>
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="pt-4 border-t border-border">
                <div className="flex items-end justify-between">
                  <p className="text-muted-foreground font-sans text-sm">Total Price</p>
                  <p className="text-3xl font-display font-bold text-primary">${vehicle.price}</p>
                </div>
                <p className="text-xs text-muted-foreground font-sans text-right">
                  {routeType === "round-trip" ? "round trip" : "one way"}
                </p>
              </div>

              {/* Included */}
              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground font-sans uppercase tracking-wide mb-3">Included</p>
                <div className="space-y-2">
                  {includedServices.map((s) => (
                    <div key={s.label} className="flex items-center gap-2">
                      <s.icon className="w-4 h-4 text-primary" />
                      <span className="text-foreground font-sans text-sm">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BookingPage;
